// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type SavingHolding } from '@shared/data-access/interfaces/api/engine/citec';
import { AppIcon } from '@shared/ui-kit/components/app-icon';
import { type EditPortfolioConfig } from '@advisor-app/features/my-research';
import { editSavedPortfolio } from '@advisor-app/features/my-research/utils/edit-saved-portfolio';
import {
  MODAL_TEMPLATES_OPTIONS,
  showModal,
} from '@shared/ui-kit/components/popups/show-modal';
import { type PortfolioLoaded } from '@shared/data-access/interfaces/common/portfolio-loaded';
import { savePortfolio } from '@advisor-app/features/utils/save-portfolio';
import {
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAppSelector } from '@advisor-app/store/store';
import {
  selectIdeas,
  selectIdeasTableData,
} from '@advisor-app/store/slices/my-research';
import { useTranslation } from 'react-i18next';

export interface UseAddPortfolioModalProps {
  onClose?: VoidFunction;
  editPortfolioConfig: EditPortfolioConfig;
}

export const useAddPortfolioModal = ({
  onClose,
  editPortfolioConfig,
}: UseAddPortfolioModalProps) => {
  const { t } = useTranslation();
  const { portfolioHoldings: holdings, portfolioBenchmark } = editPortfolioConfig;

  const [positions, setPositions] = useState<PortfolioLoaded[]>(holdings);
  const [updatedBenchmark, setUpdatedBenchmark] = useState(portfolioBenchmark);
  const [updatedPortfolioName, setUpdatedPortfolioName] = useState('');

  const [hasChanges, setHasChanges] = useState(false);

  const portfolios = useAppSelector(state =>
    selectIdeas(state).map(idea => idea.portfolio),
  );

  const savedPortfolios = useAppSelector(selectIdeasTableData);

  const filteredPositions = useMemo(
    () => positions.filter(({ ticker }) => ticker !== '$CASH'),
    [positions],
  );

  const handleUpdateBenchmark = useCallback(
    (value: string, portfolio?: boolean) => {
      if (portfolio) {
        const benchmark = savedPortfolios
          .find(portfolio => portfolio.portfolio_id.toString() === value)
          ?.subRows.slice()
          .sort((a, b) => b.weight! - a.weight!)[0].ticker;

        setUpdatedPortfolioName(benchmark!);
      } else setUpdatedBenchmark(value);

      setHasChanges(true);
    },
    [savedPortfolios],
  );

  const onSetPositions = useCallback(
    (positions: SetStateAction<PortfolioLoaded[]>) => {
      setPositions(positions);
      setHasChanges(true);
    },
    [],
  );

  const updatePosition = useCallback(
    (rowIndex: number, columnId: string, value: any) => {
      let newValue: string | number;
      /* istanbul ignore else */
      if (
        columnId === 'outstanding_balance' ||
        columnId === 'unrealized_capital_gains' ||
        columnId === 'weight'
      ) {
        newValue = Number(value);

        if (isNaN(value)) {
          return;
        }
      } else {
        newValue = value;
      }

      setPositions(prev =>
        prev.map((row, index) => {
          if (index === rowIndex) {
            return {
              ...prev[rowIndex],
              [columnId]: newValue,
            };
          }
          return row;
        }),
      );

      setHasChanges(true);
    },
    [],
  );

  const onDeletePositions = useCallback(async () => {
    const { isConfirmed } = await showModal({
      ...MODAL_TEMPLATES_OPTIONS(t).CONFIRM_DELETE,
      icon: <AppIcon icon="ExclamationMark" fill="#1072BA" />,
      title: t('dashboard.delete-positions', { ns: 'dashboard' }),
      content: t('dashboard.delete-positions-confirmation', { ns: 'dashboard' }),
    });

    if (isConfirmed) {
      setUpdatedPortfolioName('');
      setPositions([]);
    }
  }, [t]);

  const onCloseAddPortfolioModal = useCallback(() => {
    setHasChanges(false);
    setPositions([]);
    onClose?.();
    setUpdatedPortfolioName('');
    setUpdatedBenchmark('');
  }, [onClose]);

  const handleEditPortfolioName = useCallback((text: string) => {
    setHasChanges(true);
    setUpdatedPortfolioName(text);
  }, []);

  const onSavePortfolio = useCallback(() => {
    const savingHoldings: SavingHolding[] = positions.map(holding => ({
      ticker: holding.ticker,
      weight: holding.weight / 100,
      unrealized_capital_gains: holding.unrealized_capital_gains,
    }));

    savePortfolio({
      onClose: () => {
        setUpdatedPortfolioName('');
        setUpdatedBenchmark('');
        onCloseAddPortfolioModal();
      },
      benchmark: updatedBenchmark,
      savingHoldings,
      portfolios,
      t,
    });
  }, [updatedBenchmark, onCloseAddPortfolioModal, portfolios, positions, t]);

  const onEditPortfolio = useCallback(() => {
    const savingHoldings: SavingHolding[] = positions.map(holding => ({
      ticker: holding.ticker,
      weight: holding.weight / 100,
      unrealized_capital_gains: holding.unrealized_capital_gains,
    }));

    editSavedPortfolio({
      editPortfolioConfig,
      updatedPortfolioName,
      positions: savingHoldings,
      benchmark: updatedBenchmark,
      onClose: () => {
        setUpdatedPortfolioName('');
        onCloseAddPortfolioModal();
      },
      portfolios,
      t,
    });
  }, [
    editPortfolioConfig,
    updatedPortfolioName,
    positions,
    updatedBenchmark,
    portfolios,
    onCloseAddPortfolioModal,
    t,
  ]);

  useEffect(() => {
    if (holdings.length > 0) {
      setPositions(holdings);
    }
  }, [holdings, setPositions]);

  return {
    positions,
    updatedBenchmark,
    handleUpdateBenchmark,
    setPositions: onSetPositions,
    filteredPositions,
    updatedPortfolioName,
    updatePosition,
    onDeletePositions,
    onSavePortfolio,
    onEditPortfolio,
    onCloseAddPortfolioModal,
    handleEditPortfolioName,
    hasChanges,
  };
};
